import { ArrowUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import Tooltip from 'components/layout/tooltip';
import {
  compassDirectionDegrees,
  compassDirectionTooltipPlacements,
  exposureOptionsForCompass
} from 'data/constants';
import { ExposureDirectionForCompassType } from 'data/types';
import { JSXElementConstructor, SVGProps, useState } from 'react';

type CompassPopoverProps = {
  label: string;
  onClick: (direction: ExposureDirectionForCompassType) => void;
  icon?: JSXElementConstructor<SVGProps<SVGSVGElement>>;
  iconSize?: 'sm' | 'md' | 'lg';
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
};

export default function CompassPopover({
  label,
  onClick,
  icon,
  iconSize = 'sm',
  iconPosition = 'left',
  disabled
}: CompassPopoverProps) {
  const [hoveringDirection, setHoveringDirection] =
    useState<ExposureDirectionForCompassType | null>(null);

  const Icon = icon;

  const iconClasses = clsx(
    'h-4 w-4',
    iconSize === 'sm' && 'h-4 w-4',
    iconSize === 'md' && 'h-5 w-5',
    iconSize === 'lg' && 'h-6 w-6'
  );

  const triggerClasses = clsx(
    'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-100 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
    iconPosition === 'right' && 'flex-row-reverse'
  );

  return (
    <Popover placement='bottom-start'>
      <div>
        <PopoverTrigger
          className={triggerClasses}
          onClick={() => setHoveringDirection(null)}
          disabled={disabled}
        >
          {Icon && <Icon className={iconClasses} />}
          {label}
        </PopoverTrigger>
      </div>
      <PopoverContent className='z-50 flex w-28 flex-wrap rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none'>
        {exposureOptionsForCompass.map((direction, index) => {
          const directionAbbreviation = direction
            .split(' ')
            .map((word) => word[0])
            .join('');

          return index === 4 ? (
            <div
              className='flex h-8 w-8 items-center justify-center text-gray-900'
              key={direction}
            >
              {hoveringDirection && (
                <ArrowUpIcon
                  className='h-4 w-4'
                  style={{
                    transform: `rotate(${compassDirectionDegrees[hoveringDirection]}deg)`
                  }}
                />
              )}
            </div>
          ) : (
            <PopoverClose
              key={direction}
              onClick={() => onClick(direction)}
              className='relative flex h-8 w-8 items-center justify-center rounded text-sm font-semibold text-gray-900 hover:bg-gray-100'
              onMouseEnter={() => setHoveringDirection(direction)}
              onMouseLeave={() => setHoveringDirection(null)}
            >
              <span>{directionAbbreviation}</span>
              <Tooltip
                text={`${direction} Facing`}
                options={{
                  placement: compassDirectionTooltipPlacements[direction]
                }}
              />
            </PopoverClose>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
